/**
 * Analytics Service for TCVelik
 * Handles Google Analytics tracking with consent management
 */

class AnalyticsService {
  constructor() {
    this.isInitialized = false;
    this.hasConsent = false;
    this.checkConsent();
  }

  /**
   * Check if user has given consent for analytics
   */
  checkConsent() {
    this.hasConsent = localStorage.getItem('analytics-consent') === 'accepted';
    this.isInitialized = this.hasConsent && typeof window.gtag === 'function';
    return this.hasConsent;
  }

  /**
   * Track a page view
   * @param {string} pageTitle - Title of the page
   * @param {string} pagePath - Path of the page
   * @param {string} pageLocation - Full URL of the page
   */
  trackPageView(pageTitle, pagePath, pageLocation) {
    if (!this.hasConsent || !this.isInitialized) {
      console.log('Analytics: No consent or not initialized, skipping page view');
      return;
    }

    try {
      window.gtag('event', 'page_view', {
        page_title: pageTitle,
        page_location: pageLocation,
        page_path: pagePath
      });
      console.log('Analytics: Page view tracked', { pageTitle, pagePath });
    } catch (error) {
      console.error('Analytics: Error tracking page view', error);
    }
  }

  /**
   * Track a custom event
   * @param {string} eventName - Name of the event
   * @param {object} parameters - Event parameters
   */
  trackEvent(eventName, parameters = {}) {
    if (!this.hasConsent || !this.isInitialized) {
      console.log('Analytics: No consent or not initialized, skipping event');
      return;
    }

    try {
      window.gtag('event', eventName, parameters);
      console.log('Analytics: Event tracked', { eventName, parameters });
    } catch (error) {
      console.error('Analytics: Error tracking event', error);
    }
  }

  /**
   * Track user login
   * @param {string} method - Login method (email, google, etc.)
   */
  trackLogin(method = 'unknown') {
    this.trackEvent('login', {
      method: method
    });
  }

  /**
   * Track user registration
   * @param {string} method - Registration method
   */
  trackSignUp(method = 'unknown') {
    this.trackEvent('sign_up', {
      method: method
    });
  }

  /**
   * Track onboarding creation
   * @param {string} onboardingId - ID of the onboarding
   */
  trackOnboardingCreated(onboardingId) {
    this.trackEvent('onboarding_created', {
      onboarding_id: onboardingId
    });
  }

  /**
   * Track onboarding completion
   * @param {string} onboardingId - ID of the onboarding
   */
  trackOnboardingCompleted(onboardingId) {
    this.trackEvent('onboarding_completed', {
      onboarding_id: onboardingId
    });
  }

  /**
   * Track search
   * @param {string} searchTerm - Search term
   * @param {number} resultCount - Number of results
   */
  trackSearch(searchTerm, resultCount = 0) {
    this.trackEvent('search', {
      search_term: searchTerm,
      result_count: resultCount
    });
  }

  /**
   * Track file download
   * @param {string} fileName - Name of the file
   * @param {string} fileType - Type of the file
   */
  trackDownload(fileName, fileType) {
    this.trackEvent('file_download', {
      file_name: fileName,
      file_type: fileType
    });
  }

  /**
   * Update consent status
   * @param {boolean} hasConsent - Whether user has consented
   */
  updateConsent(hasConsent) {
    this.hasConsent = hasConsent;
    this.isInitialized = hasConsent && typeof window.gtag === 'function';
    
    if (hasConsent) {
      console.log('Analytics: Consent granted, analytics enabled');
    } else {
      console.log('Analytics: Consent revoked, analytics disabled');
    }
  }

  /**
   * Check if analytics is ready
   */
  isReady() {
    return this.hasConsent && this.isInitialized;
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

// Listen for consent changes from the consent banner
window.addEventListener('storage', (e) => {
  if (e.key === 'analytics-consent') {
    analyticsService.updateConsent(e.newValue === 'accepted');
  }
});

export default analyticsService;
